export type GuidedFlowAnswer = {
  key: string;
  label: string;
  description?: string;
  searchKeywords?: string[];
  nextNodeId: string;
};

export type GuidedFlowQuestionNode = {
  id: string;
  type: 'question';
  title: string;
  question: string;
  helperText?: string;
  searchKeywords?: string[];
  answers: GuidedFlowAnswer[];
};

export type GuidedFlowResultNode = {
  id: string;
  type: 'result';
  title: string;
  summary: string;
  nextSteps: string[];
  supportText?: string;
  searchKeywords?: string[];
};

export type GuidedFlowNode = GuidedFlowQuestionNode | GuidedFlowResultNode;

export type GuidedFlow = {
  key: string;
  title: string;
  description: string;
  searchKeywords?: string[];
  startNodeId: string;
  nodes: Record<string, GuidedFlowNode>;
};

export function getGuidedFlowContentIssues(flow: GuidedFlow): string[] {
  const issues: string[] = [];

  if (!flow.nodes[flow.startNodeId]) {
    issues.push(`${flow.key}: start node "${flow.startNodeId}" does not exist`);
  }

  Object.keys(flow.nodes).forEach((nodeId) => {
    const node = flow.nodes[nodeId];

    if (node.type !== 'question') {
      return;
    }

    if (node.answers.length === 0) {
      issues.push(`${flow.key}: question node "${node.id}" has no answers`);
    }

    node.answers.forEach((answer) => {
      if (!flow.nodes[answer.nextNodeId]) {
        issues.push(
          `${flow.key}: answer "${answer.key}" in node "${node.id}" points to missing node "${answer.nextNodeId}"`
        );
      }
    });
  });

  return issues;
}

export function getGuidedFlowNode(flow: GuidedFlow, nodeId: string): GuidedFlowNode {
  const node = flow.nodes[nodeId];

  if (!node) {
    return flow.nodes[flow.startNodeId];
  }

  return node;
}

export function isGuidedFlowResultNode(node: GuidedFlowNode): node is GuidedFlowResultNode {
  return node.type === 'result';
}

export function advanceGuidedFlow(
  currentNodeId: string,
  nextNodeId: string,
  history: string[]
): { currentNodeId: string; history: string[] } {
  return {
    currentNodeId: nextNodeId,
    history: [...history, currentNodeId],
  };
}

export function goBackGuidedFlow(
  currentNodeId: string,
  history: string[]
): { currentNodeId: string; history: string[] } {
  if (history.length === 0) {
    return {
      currentNodeId,
      history,
    };
  }

  const nextHistory = history.slice(0, -1);
  const previousNodeId = history[history.length - 1];

  return {
    currentNodeId: previousNodeId,
    history: nextHistory,
  };
}
